import { flip, Placement, shift, useFloating } from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";
import {
  ComponentPublicInstance,
  ComputedRef,
  nextTick,
  readonly,
  ref,
  Ref,
  StyleValue,
  watch,
} from "vue";

export type DropdownPlacement = Placement;

export type DropdownOptions = {
  placement?: DropdownPlacement;
};

export type DropdownInstance = {
  isVisible: Readonly<Ref<boolean>>;
  style: ComputedRef<StyleValue>;
  toggle: (event?: Event) => void;
  setVisible: (value: boolean, event?: Event) => void;
};

const defaultOptions: DropdownOptions = {
  placement: "bottom-start",
};

export function useDropdown(
  trigger: Ref<HTMLElement | null | undefined> | Ref<ComponentPublicInstance>,
  dropdown: Ref<HTMLElement | null | undefined> | Ref<ComponentPublicInstance>,
  options: DropdownOptions = {}
): DropdownInstance {
  if (!options) {
    options = {};
  }

  let lastTriggerEvent: Event | null;
  const isVisible: Ref<boolean> = ref(false);

  const toggle = (event?: Event) => {
    setVisible(!isVisible.value, event);
  };

  const setVisible = (value: boolean, event?: Event) => {
    lastTriggerEvent = event ?? null;
    isVisible.value = value;
  };

  const clickOutsideHandler = (event: MouseEvent | TouchEvent) => {
    if (lastTriggerEvent && lastTriggerEvent.target === event.target) {
      lastTriggerEvent = null;
      return;
    }
    isVisible.value = false;
  };

  const { floatingStyles, update } = useFloating(trigger, dropdown, {
    placement: options.placement ?? defaultOptions.placement,
    middleware: [flip(), shift()],
  });

  onClickOutside(dropdown, clickOutsideHandler);

  // Only listen to outside click when visible.
  watch(isVisible, (value) => {
    if (value) {
      nextTick(update);
    }
  });

  return {
    isVisible: readonly(isVisible),
    style: <ComputedRef<StyleValue>>floatingStyles,
    toggle,
    setVisible,
  };
}
